/**
 * TenantConfigurationsSection
 *
 * Admin-only (roleId ≤ 2) section for managing tenant-level customizations.
 * Displays for the tenants in user.mappings, supports full CRUD with the same
 * rich tabbed editor as UserConfigurationsSection (Settings / Voice / Categories /
 * Items / Custom Items / Favorites), stored as MobileAppCustomizations JSON.
 */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  Business as TenantIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Favorite as FavoriteIcon,
  MusicNote as AudioIcon,
  PlayArrow as PlayIcon,
  RecordVoiceOver as VoiceIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Stop as StopIcon,
  ViewList as ListIcon,
  GridView as ItemsIcon,
} from '@mui/icons-material';

import {
  GET_TENANT_CUSTOMIZATIONS,
  GET_VOICES,
  GET_CATEGORIES,
  GET_ITEMS_BASIC,
  GET_ITEMS_BY_CATEGORY,
} from '../graphql/queries';
import {
  CREATE_TENANT_CUSTOMIZATION,
  UPDATE_TENANT_CUSTOMIZATION,
  DELETE_TENANT_CUSTOMIZATION,
  GENERATE_AUDIO,
  GENERATE_IMAGE,
} from '../graphql/mutations';
import {
  TenantCustomization,
  User,
  MobileAppCustomizations,
  Voice,
  Item,
  Category,
  CategoryOrderEntry,
  ItemOrderEntry,
  CustomItemEntry,
  FavoriteItem,
} from '../types';

// ─── constants ───────────────────────────────────────────────────────────────

const CUSTOMIZATION_TYPE_OPTIONS = [
  { value: 'tenant_app_settings', label: 'App Settings' },
  { value: 'tenant_defaults',     label: 'Default Overrides' },
  { value: 'tenant_branding',     label: 'Branding' },
];

const EMPTY_CONFIG: MobileAppCustomizations = {
  recentlyUsedItems: [],
  itemOrder: {},
  itemVisibility: {},
  customItems: [],
  categoryOrder: [],
  userPreferences: {},
  userSettings: undefined,
  favorites: [],
  lastUpdated: new Date().toISOString(),
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function parseCustomData(raw: string): MobileAppCustomizations | null {
  try { return JSON.parse(raw) as MobileAppCustomizations; } catch { return null; }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function resolveElevenLabsVoiceId(storedId: string | undefined, allVoices: Voice[]): string | undefined {
  if (!storedId || !allVoices.length) return undefined;
  return allVoices.find((v) => v.id === storedId || v.voiceId === storedId)?.voiceId;
}

// ─── sub-components ──────────────────────────────────────────────────────────

interface TabPanelProps { value: number; index: number; children: React.ReactNode; }
const TabPanel: React.FC<TabPanelProps> = ({ value, index, children }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

// ─── Edit Dialog ─────────────────────────────────────────────────────────────

interface BasicItem { code: string; name: string; position: number; categoryCode: string; status: number; }

interface EditDialogProps {
  open: boolean;
  customization: TenantCustomization | null; // null = create mode
  allCustomizations: TenantCustomization[];
  allVoices: Voice[];
  tenantId: string;
  onClose: () => void;
  onSaved: (saved: TenantCustomization) => void;
}

const EditDialog: React.FC<EditDialogProps> = ({
  open,
  customization,
  allCustomizations,
  allVoices,
  tenantId,
  onClose,
  onSaved,
}) => {
  const isCreateMode = !customization;

  const [label, setLabel] = useState(customization?.label ?? 'New Configuration');
  const [customizationType, setCustomizationType] = useState(
    customization?.customizationType ?? 'tenant_app_settings'
  );
  const [config, setConfig] = useState<MobileAppCustomizations>(
    parseCustomData(customization?.customData ?? '{}') ?? { ...EMPTY_CONFIG, lastUpdated: new Date().toISOString() }
  );
  const [tab, setTab] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [playingItemCode, setPlayingItemCode] = useState<string | null>(null);
  const [selectedItemCategory, setSelectedItemCategory] = useState<string>('');

  const [createMutation, { loading: creating }] = useMutation(CREATE_TENANT_CUSTOMIZATION);
  const [updateMutation, { loading: updating }] = useMutation(UPDATE_TENANT_CUSTOMIZATION);
  const [generateAudioMutation] = useMutation(GENERATE_AUDIO);
  const [generateImageMutation] = useMutation(GENERATE_IMAGE);
  const saving = creating || updating;

  const { data: defaultCatsData, loading: loadingDefaultCats } = useQuery(GET_CATEGORIES, {
    skip: !open, fetchPolicy: 'cache-first',
  });
  const { data: defaultItemsData, loading: loadingDefaultItems } = useQuery(GET_ITEMS_BASIC, {
    skip: !open, fetchPolicy: 'cache-first',
  });
  const loadingDefaults = isCreateMode && (loadingDefaultCats || loadingDefaultItems);

  // Reset form when dialog opens
  useEffect(() => {
    if (!open) return;
    setLabel(customization?.label ?? 'New Configuration');
    setCustomizationType(customization?.customizationType ?? 'tenant_app_settings');
    setConfig(parseCustomData(customization?.customData ?? '{}') ?? { ...EMPTY_CONFIG, lastUpdated: new Date().toISOString() });
    setTab(0);
    setSelectedItemCategory('');
    setSaveError(null);
    setShowAddCustom(false);
    setShowAddFav(false);
    resetCustomItemForm();
    setNewFavCat('');
    setNewFavItem('');
  }, [open, customization]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate categoryOrder + itemOrder for create mode
  useEffect(() => {
    if (!isCreateMode || !open || !defaultCatsData || !defaultItemsData) return;
    const cats: Category[] = [...(defaultCatsData.categories as Category[])]
      .filter((c) => c.status === 1)
      .sort((a, b) => a.createdDate.localeCompare(b.createdDate));
    const allItems = defaultItemsData.items as BasicItem[];
    const categoryOrder: CategoryOrderEntry[] = cats.map((cat, idx) => ({
      categoryCode: cat.code, name: cat.name, position: idx + 1, isVisible: true,
    }));
    const itemOrder: Record<string, ItemOrderEntry[]> = {};
    for (const cat of cats) {
      const catItems = allItems
        .filter((item) => item.categoryCode === cat.code && item.status === 1)
        .sort((a, b) => a.position - b.position);
      if (catItems.length > 0) {
        itemOrder[cat.code] = catItems.map((item, idx) => ({
          itemCode: item.code, name: item.name, position: idx + 1,
        }));
      }
    }
    setConfig((prev) => ({ ...prev, categoryOrder, itemOrder, itemVisibility: {}, lastUpdated: new Date().toISOString() }));
  }, [isCreateMode, open, defaultCatsData, defaultItemsData]);

  // Assign IDs to legacy custom items that lack them
  useEffect(() => {
    if (isCreateMode) return;
    setConfig((prev) => {
      if ((prev.customItems ?? []).every((it) => !!it.id)) return prev;
      return {
        ...prev,
        customItems: (prev.customItems ?? []).map((item, idx) => ({
          ...item, id: item.id || `custom_${item.code || idx}_legacy`,
        })),
      };
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: categoryItemsData, loading: categoryItemsLoading } = useQuery(GET_ITEMS_BY_CATEGORY, {
    variables: { categoryCode: selectedItemCategory },
    skip: !open || !selectedItemCategory,
    fetchPolicy: 'cache-first',
  });

  const apiItemsMap = useMemo<Record<string, Item>>(() => {
    const map: Record<string, Item> = {};
    for (const item of (categoryItemsData?.itemsByCategory ?? []) as Item[]) map[item.code] = item;
    return map;
  }, [categoryItemsData]);

  // ── save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaveError(null);
    const updatedConfig: MobileAppCustomizations = { ...config, lastUpdated: new Date().toISOString() };
    const input = {
      tenantId,
      customizationType,
      label: label.trim() || 'New Configuration',
      customData: JSON.stringify(updatedConfig),
      isActive: true,
      version: isCreateMode ? 1 : (customization!.version ?? 1) + 1,
    };
    try {
      let result: TenantCustomization | undefined;
      if (isCreateMode) {
        const { data } = await createMutation({ variables: { input } });
        result = data?.createTenantCustomization;
      } else {
        const { data } = await updateMutation({ variables: { id: customization!.id, input } });
        result = data?.updateTenantCustomization;
      }
      if (result) { onSaved(result); onClose(); }
      else setSaveError('Save returned no result — please try again.');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save configuration.');
    }
  };

  // ── audio playback ────────────────────────────────────────────────────────
  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingId(null);
    setPlayingItemCode(null);
  };

  const playCustomItemAudio = (item: CustomItemEntry) => {
    if (playingId === item.id) { stopAudio(); return; }
    stopAudio();
    const elevenLabsVoiceId = resolveElevenLabsVoiceId(config.userPreferences?.defaultVoice, allVoices);
    let audioData: string | undefined;
    if (item.multiVoiceAudioData && item.multiVoiceAudioData.length > 0) {
      const match = elevenLabsVoiceId ? item.multiVoiceAudioData.find((m) => m.voiceId === elevenLabsVoiceId) : undefined;
      audioData = match?.audioData ?? item.multiVoiceAudioData[0]?.audioData;
    }
    if (!audioData) {
      if ('speechSynthesis' in window) {
        const u = new SpeechSynthesisUtterance(item.name);
        window.speechSynthesis.speak(u);
        setPlayingId(item.id);
        u.onend = () => setPlayingId(null);
      }
      return;
    }
    try {
      const src = audioData.startsWith('data:') ? audioData : `data:audio/mpeg;base64,${audioData}`;
      const audio = new Audio(src);
      audioRef.current = audio;
      setPlayingId(item.id);
      audio.play();
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => setPlayingId(null);
    } catch { setPlayingId(null); }
  };

  const playStandardItemAudio = (itemCode: string, itemName: string) => {
    if (playingItemCode === itemCode) { stopAudio(); return; }
    stopAudio();
    const apiItem = apiItemsMap[itemCode];
    if (!apiItem?.soundOptions?.length) {
      if ('speechSynthesis' in window) {
        const u = new SpeechSynthesisUtterance(itemName);
        window.speechSynthesis.speak(u);
        setPlayingItemCode(itemCode);
        u.onend = () => setPlayingItemCode(null);
      }
      return;
    }
    const elevenLabsVoiceId = resolveElevenLabsVoiceId(config.userPreferences?.defaultVoice, allVoices);
    const option = elevenLabsVoiceId
      ? (apiItem.soundOptions.find((s) => s.voiceId === elevenLabsVoiceId) ?? apiItem.soundOptions[0])
      : apiItem.soundOptions[0];
    const rawData = option.soundData;
    if (!rawData) return;
    try {
      const src = rawData.startsWith('data:') ? rawData : `data:audio/mpeg;base64,${rawData}`;
      const audio = new Audio(src);
      audioRef.current = audio;
      setPlayingItemCode(itemCode);
      audio.play();
      audio.onended = () => setPlayingItemCode(null);
      audio.onerror = () => setPlayingItemCode(null);
    } catch { setPlayingItemCode(null); }
  };

  // ── config mutators ───────────────────────────────────────────────────────
  const setUserSettings = (partial: Partial<NonNullable<typeof config.userSettings>>) => {
    setConfig((prev) => ({
      ...prev,
      userSettings: {
        isAiEnabled: false, enableHapticFeedback: true, enableSoundEffects: true,
        enableDarkMode: false, autoSyncEnabled: true, showRecentItemCount: true,
        lastUpdated: new Date().toISOString(), lastUpdatedBy: 'web',
        ...prev.userSettings, ...partial,
      },
    }));
  };

  const setDefaultVoice = (mongoId: string) => {
    setConfig((prev) => ({ ...prev, userPreferences: { ...prev.userPreferences, defaultVoice: mongoId } }));
  };

  const setCategoryVisible = (categoryCode: string, isVisible: boolean) => {
    setConfig((prev) => ({
      ...prev,
      categoryOrder: prev.categoryOrder.map((c) => c.categoryCode === categoryCode ? { ...c, isVisible } : c),
    }));
  };

  const moveCategoryUp = (index: number) => {
    if (index === 0) return;
    setConfig((prev) => {
      const arr = [...prev.categoryOrder];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return { ...prev, categoryOrder: arr.map((c, i) => ({ ...c, position: i })) };
    });
  };

  const moveCategoryDown = (index: number) => {
    setConfig((prev) => {
      if (index >= prev.categoryOrder.length - 1) return prev;
      const arr = [...prev.categoryOrder];
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return { ...prev, categoryOrder: arr.map((c, i) => ({ ...c, position: i })) };
    });
  };

  const setItemVisible = (categoryCode: string, itemCode: string, isVisible: boolean) => {
    setConfig((prev) => {
      const prevVis = prev.itemVisibility ?? {};
      const existing = prevVis[categoryCode] ?? [];
      const idx = existing.findIndex((e) => e.itemCode === itemCode);
      const updated = idx >= 0
        ? existing.map((e) => (e.itemCode === itemCode ? { ...e, isVisible } : e))
        : [...existing, { itemCode, isVisible }];
      return { ...prev, itemVisibility: { ...prevVis, [categoryCode]: updated } };
    });
  };

  const moveItemUp = (categoryCode: string, index: number) => {
    if (index === 0) return;
    setConfig((prev) => {
      const prevOrd = prev.itemOrder ?? {};
      const arr = [...(prevOrd[categoryCode] ?? [])];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return { ...prev, itemOrder: { ...prevOrd, [categoryCode]: arr.map((e, i) => ({ ...e, position: i })) } };
    });
  };

  const moveItemDown = (categoryCode: string, index: number) => {
    setConfig((prev) => {
      const prevOrd = prev.itemOrder ?? {};
      const arr = [...(prevOrd[categoryCode] ?? [])];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return { ...prev, itemOrder: { ...prevOrd, [categoryCode]: arr.map((e, i) => ({ ...e, position: i })) } };
    });
  };

  const deleteCustomItem = (itemId: string) => {
    setConfig((prev) => ({ ...prev, customItems: (prev.customItems ?? []).filter((ci) => ci.id !== itemId) }));
  };

  const removeFavorite = (itemCode: string) => {
    setConfig((prev) => ({ ...prev, favorites: (prev.favorites ?? []).filter((f) => f.itemCode !== itemCode) }));
  };

  // ── add custom item form ──────────────────────────────────────────────────
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [newCustomName, setNewCustomName] = useState('');
  const [newCustomCode, setNewCustomCode] = useState('');
  const [newCustomCodeEdited, setNewCustomCodeEdited] = useState(false);
  const [newCustomCat, setNewCustomCat] = useState('');
  const [newCustomDesc, setNewCustomDesc] = useState('');
  const [newCustomAudioText, setNewCustomAudioText] = useState('');
  const [newCustomAudioTextEdited, setNewCustomAudioTextEdited] = useState(false);
  const [newCustomImageData, setNewCustomImageData] = useState('');
  const [newCustomMultiVoiceAudio, setNewCustomMultiVoiceAudio] = useState<{ voiceId: string; audioData: string }[]>([]);
  const [genAudioLoading, setGenAudioLoading] = useState(false);
  const [genImageLoading, setGenImageLoading] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [editingCustomItemId, setEditingCustomItemId] = useState<string | null>(null);

  const toPascalCase = (s: string) =>
    s.split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('').replace(/[^a-zA-Z0-9]/g, '');

  const resetCustomItemForm = () => {
    setNewCustomName(''); setNewCustomCode(''); setNewCustomCodeEdited(false);
    setNewCustomCat(''); setNewCustomDesc(''); setNewCustomAudioText('');
    setNewCustomAudioTextEdited(false); setNewCustomImageData('');
    setNewCustomMultiVoiceAudio([]); setGenError(null); setEditingCustomItemId(null);
  };

  const startEditCustomItem = (item: CustomItemEntry) => {
    setNewCustomName(item.name); setNewCustomCode(item.code); setNewCustomCodeEdited(true);
    setNewCustomCat(item.categoryCode); setNewCustomDesc(item.description ?? '');
    setNewCustomAudioText(item.audioText ?? ''); setNewCustomAudioTextEdited(!!item.audioText);
    setNewCustomImageData(item.imageData ?? ''); setNewCustomMultiVoiceAudio(item.multiVoiceAudioData ?? []);
    setGenError(null); setEditingCustomItemId(item.id); setShowAddCustom(true);
  };

  const generateCustomItemImage = async () => {
    const name = newCustomName.trim();
    if (!name) return;
    setGenImageLoading(true); setGenError(null);
    try {
      const prompt = `PECS AAC communication card for "${name}": simple, clear, cartoon-style illustration on white background`;
      const { data } = await generateImageMutation({ variables: { input: { prompt, size: '1024x1024', quality: 'standard', returnBase64: true } } });
      if (data?.generateImage?.imageBase64)
        setNewCustomImageData(`data:image/png;base64,${data.generateImage.imageBase64}`);
    } catch (e: unknown) { setGenError(e instanceof Error ? e.message : 'Failed to generate image'); }
    finally { setGenImageLoading(false); }
  };

  const generateCustomItemAudio = async () => {
    const text = newCustomAudioText.trim();
    if (!text || allVoices.length === 0) return;
    setGenAudioLoading(true); setGenError(null);
    try {
      const results: { voiceId: string; audioData: string }[] = [];
      for (const voice of allVoices) {
        if (!voice.voiceId) continue;
        const { data } = await generateAudioMutation({ variables: { input: { text, voiceId: voice.voiceId } } });
        if (data?.generateAudio?.audioBase64)
          results.push({ voiceId: voice.voiceId, audioData: data.generateAudio.audioBase64 });
      }
      setNewCustomMultiVoiceAudio(results);
    } catch (e: unknown) { setGenError(e instanceof Error ? e.message : 'Failed to generate audio'); }
    finally { setGenAudioLoading(false); }
  };

  const addCustomItem = () => {
    const name = newCustomName.trim();
    const code = newCustomCode.trim();
    if (!name || !code || !newCustomCat) return;
    if (editingCustomItemId) {
      setConfig((prev) => ({
        ...prev,
        customItems: (prev.customItems ?? []).map((it) =>
          it.id === editingCustomItemId
            ? { ...it, name, code, description: newCustomDesc.trim() || undefined, categoryCode: newCustomCat, audioText: newCustomAudioText.trim() || undefined, imageData: newCustomImageData || undefined, multiVoiceAudioData: newCustomMultiVoiceAudio.length > 0 ? newCustomMultiVoiceAudio : undefined }
            : it
        ),
      }));
    } else {
      const item: CustomItemEntry = {
        id: `custom_${Date.now()}`, code, name,
        description: newCustomDesc.trim() || undefined,
        categoryCode: newCustomCat,
        audioText: newCustomAudioText.trim() || undefined,
        imageData: newCustomImageData || undefined,
        multiVoiceAudioData: newCustomMultiVoiceAudio.length > 0 ? newCustomMultiVoiceAudio : undefined,
        createdAt: new Date().toISOString(), isCustom: true,
      };
      setConfig((prev) => ({ ...prev, customItems: [...(prev.customItems ?? []), item] }));
    }
    resetCustomItemForm();
    setShowAddCustom(false);
  };

  // ── add favorite form ────────────────────────────────────────────────────
  const [showAddFav, setShowAddFav] = useState(false);
  const [newFavCat, setNewFavCat] = useState('');
  const [newFavItem, setNewFavItem] = useState('');

  const allCatsForPicker: Category[] = ((defaultCatsData?.categories ?? []) as Category[])
    .filter((c) => c.status === 1)
    .sort((a, b) => a.createdDate.localeCompare(b.createdDate));
  const allItemsForPicker: BasicItem[] = (defaultItemsData?.items ?? []) as BasicItem[];
  const itemsForFavPicker = allItemsForPicker
    .filter((i) => i.categoryCode === newFavCat && i.status === 1)
    .sort((a, b) => a.name.localeCompare(b.name));

  const addFavorite = () => {
    if (!newFavItem || !newFavCat) return;
    if ((config.favorites ?? []).some((f) => f.itemCode === newFavItem && f.categoryCode === newFavCat)) return;
    const fav: FavoriteItem = {
      itemCode: newFavItem, categoryCode: newFavCat,
      addedDate: new Date().toISOString(), isAutoGenerated: false,
      position: (config.favorites ?? []).length + 1, source: 'user',
    };
    setConfig((prev) => ({ ...prev, favorites: [...(prev.favorites ?? []), fav] }));
    setNewFavCat(''); setNewFavItem(''); setShowAddFav(false);
  };

  // ── derived display data ──────────────────────────────────────────────────
  const settings = config.userSettings;
  const categories: CategoryOrderEntry[] = [...(config.categoryOrder ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const customItems: CustomItemEntry[] = config.customItems ?? [];
  const favorites: FavoriteItem[] = config.favorites ?? [];
  const defaultVoiceId = config.userPreferences?.defaultVoice;

  const itemOrder = config.itemOrder ?? {};
  const itemVisibility = config.itemVisibility ?? {};

  const categoriesWithItems = categories.filter(
    (cat) => (itemOrder[cat.categoryCode]?.length ?? 0) > 0
  );
  const activeCatCode = selectedItemCategory || (categoriesWithItems[0]?.categoryCode ?? '');
  const itemsForCategory = (() => {
    if (!activeCatCode) return [];
    const order = [...(itemOrder[activeCatCode] ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const visMap: Record<string, boolean> = {};
    for (const v of itemVisibility[activeCatCode] ?? []) visMap[v.itemCode] = v.isVisible;
    return order.map((entry) => ({ code: entry.itemCode, name: entry.name ?? entry.itemCode, position: entry.position ?? 0, isVisible: visMap[entry.itemCode] ?? true }));
  })();

  const customCodeDuplicate = (code: string) => {
    const t = code.trim();
    if (!t) return false;
    if ((customItems ?? []).some((it) => it.code === t && it.id !== editingCustomItemId)) return true;
    return allCustomizations.some((rec) => {
      if (rec.id === customization?.id) return false;
      return (parseCustomData(rec.customData ?? '{}')?.customItems ?? []).some((ci) => ci.code === t);
    });
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6" fontWeight={700} component="div">
          {isCreateMode ? 'New Tenant Configuration' : 'Edit Tenant Configuration'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
          <TextField
            label="Configuration name"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Type"
            select
            value={customizationType}
            onChange={(e) => setCustomizationType(e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
            SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 200 } } } }}
          >
            {CUSTOMIZATION_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loadingDefaults ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading default categories and items…</Typography>
          </Box>
        ) : (
          <>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              <Tab icon={<SettingsIcon fontSize="small" />} label="Settings" iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
              <Tab icon={<VoiceIcon fontSize="small" />} label="Voice" iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
              <Tab icon={<ListIcon fontSize="small" />} label="Categories" iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
              <Tab icon={<ItemsIcon fontSize="small" />} label={`Items (${Object.values(config.itemOrder ?? {}).reduce((n, a) => n + a.length, 0)})`} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
              <Tab icon={<AudioIcon fontSize="small" />} label={`Custom Items (${customItems.length})`} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
              <Tab icon={<FavoriteIcon fontSize="small" />} label={`Favorites (${favorites.length})`} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.82rem' }} />
            </Tabs>

            <Box sx={{ px: 3, py: 1 }}>

              {/* ── Tab 0: Settings ── */}
              <TabPanel value={tab} index={0}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>App Behaviour</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Switch checked={settings?.isAiEnabled ?? false} onChange={(e) => setUserSettings({ isAiEnabled: e.target.checked })} color="primary" />}
                      label={<Box><Typography variant="body2" fontWeight={500}>AI Suggestions</Typography><Typography variant="caption" color="text.secondary">Enable AI-powered item ordering</Typography></Box>}
                      sx={{ alignItems: 'flex-start' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Switch checked={settings?.enableHapticFeedback ?? true} onChange={(e) => setUserSettings({ enableHapticFeedback: e.target.checked })} color="primary" />}
                      label={<Box><Typography variant="body2" fontWeight={500}>Haptic Feedback</Typography><Typography variant="caption" color="text.secondary">Vibrate on item tap</Typography></Box>}
                      sx={{ alignItems: 'flex-start' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Switch checked={settings?.enableSoundEffects ?? true} onChange={(e) => setUserSettings({ enableSoundEffects: e.target.checked })} color="primary" />}
                      label={<Box><Typography variant="body2" fontWeight={500}>Sound Effects</Typography><Typography variant="caption" color="text.secondary">UI sounds on interactions</Typography></Box>}
                      sx={{ alignItems: 'flex-start' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Switch checked={settings?.enableDarkMode ?? false} onChange={(e) => setUserSettings({ enableDarkMode: e.target.checked })} color="primary" />}
                      label={<Box><Typography variant="body2" fontWeight={500}>Dark Mode</Typography><Typography variant="caption" color="text.secondary">Use dark theme in app</Typography></Box>}
                      sx={{ alignItems: 'flex-start' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Switch checked={settings?.autoSyncEnabled ?? true} onChange={(e) => setUserSettings({ autoSyncEnabled: e.target.checked })} color="primary" />}
                      label={<Box><Typography variant="body2" fontWeight={500}>Auto Sync</Typography><Typography variant="caption" color="text.secondary">Automatically sync settings</Typography></Box>}
                      sx={{ alignItems: 'flex-start' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Switch checked={settings?.showRecentItemCount ?? true} onChange={(e) => setUserSettings({ showRecentItemCount: e.target.checked })} color="primary" />}
                      label={<Box><Typography variant="body2" fontWeight={500}>Show Recent Count</Typography><Typography variant="caption" color="text.secondary">Display recent item count badges</Typography></Box>}
                      sx={{ alignItems: 'flex-start' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Voice Speed</Typography>
                    <Typography variant="caption" color="text.secondary">{settings?.voiceSpeed != null ? `${settings.voiceSpeed}%` : 'Default (100%)'}</Typography>
                    <Slider
                      value={settings?.voiceSpeed ?? 100} min={50} max={200} step={5}
                      marks={[{ value: 50, label: '50%' }, { value: 100, label: '100%' }, { value: 200, label: '200%' }]}
                      valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
                      onChange={(_, v) => setUserSettings({ voiceSpeed: v as number })}
                      sx={{ mt: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Voice Volume</Typography>
                    <Typography variant="caption" color="text.secondary">{settings?.voiceVolume != null ? `${Math.round((settings.voiceVolume) * 100)}%` : 'Default (100%)'}</Typography>
                    <Slider
                      value={settings?.voiceVolume != null ? Math.round(settings.voiceVolume * 100) : 100}
                      min={0} max={100} step={5}
                      marks={[{ value: 0, label: '0%' }, { value: 50, label: '50%' }, { value: 100, label: '100%' }]}
                      valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
                      onChange={(_, v) => setUserSettings({ voiceVolume: (v as number) / 100 })}
                      sx={{ mt: 2 }}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              {/* ── Tab 1: Voice ── */}
              <TabPanel value={tab} index={1}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Default Voice</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Select the default voice used for speaking items under this tenant.
                </Typography>
                {allVoices.length === 0 ? (
                  <Alert severity="info">Loading voices…</Alert>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel shrink>Default voice</InputLabel>
                    <Select
                      label="Default voice"
                      value={defaultVoiceId ?? ''}
                      onChange={(e) => setDefaultVoice(e.target.value)}
                      notched
                      displayEmpty
                      MenuProps={{ disableScrollLock: true }}
                    >
                      <MenuItem value=""><em>No default voice selected</em></MenuItem>
                      {allVoices.filter((v) => v.isActive).map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {v.name}
                              {v.isDefault && <Chip label="default" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
                            </Typography>
                            {v.description && <Typography variant="caption" color="text.secondary">{v.description}</Typography>}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {defaultVoiceId && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Selected: <strong>{allVoices.find((v) => v.id === defaultVoiceId)?.name ?? 'Unknown'}</strong>
                      {' '}(ElevenLabs ID: {resolveElevenLabsVoiceId(defaultVoiceId, allVoices) ?? '—'})
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              {/* ── Tab 2: Categories ── */}
              <TabPanel value={tab} index={2}>
                {categories.length === 0 ? (
                  <Alert severity="info">No category ordering data in this configuration.</Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Category Order &amp; Visibility</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      Use the arrows to reorder. Toggle visibility to show/hide in the app.
                    </Typography>
                    <List dense disablePadding>
                      {categories.map((cat, idx) => (
                        <ListItem
                          key={cat.categoryCode ?? `cat-${idx}`}
                          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, mb: 0.75, bgcolor: cat.isVisible ? 'background.paper' : 'action.hover', opacity: cat.isVisible ? 1 : 0.6 }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                            <IconButton size="small" onClick={() => moveCategoryUp(idx)} disabled={idx === 0}>
                              <ExpandMoreIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => moveCategoryDown(idx)} disabled={idx === categories.length - 1}>
                              <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <ListItemText
                            primary={<Typography variant="body2" fontWeight={500}>{cat.name || cat.categoryCode}</Typography>}
                            secondary={<Typography variant="caption" color="text.secondary">Position: {idx + 1} · Code: {cat.categoryCode}</Typography>}
                          />
                          <ListItemSecondaryAction>
                            <FormControlLabel
                              control={<Switch checked={cat.isVisible} onChange={(e) => setCategoryVisible(cat.categoryCode, e.target.checked)} size="small" color="primary" />}
                              label={<Typography variant="caption">{cat.isVisible ? 'Visible' : 'Hidden'}</Typography>}
                              labelPlacement="start"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </TabPanel>

              {/* ── Tab 3: Items ── */}
              <TabPanel value={tab} index={3}>
                {categoriesWithItems.length === 0 ? (
                  <Alert severity="info">No item ordering data in this configuration.</Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Item Order &amp; Visibility</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      Select a category to reorder its items or toggle their visibility in the app.
                    </Typography>
                    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                      <InputLabel shrink>Category</InputLabel>
                      <Select
                        label="Category"
                        value={activeCatCode}
                        onChange={(e) => setSelectedItemCategory(e.target.value)}
                        notched
                        MenuProps={{ disableScrollLock: true }}
                      >
                        {categoriesWithItems.map((cat) => (
                          <MenuItem key={cat.categoryCode} value={cat.categoryCode}>
                            {cat.name || cat.categoryCode} ({itemOrder[cat.categoryCode]?.length ?? 0} items)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {itemsForCategory.length === 0 ? (
                      <Alert severity="info">No items for this category.</Alert>
                    ) : categoryItemsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                    ) : (
                      <List dense disablePadding>
                        {itemsForCategory.map((item, idx) => {
                          const apiItem = apiItemsMap[item.code];
                          const hasAudio = (apiItem?.soundOptions?.length ?? 0) > 0;
                          const imgRaw = apiItem?.imageData;
                          const imgSrc = imgRaw ? (imgRaw.startsWith('data:') ? imgRaw : `data:image/png;base64,${imgRaw}`) : undefined;
                          return (
                            <ListItem
                              key={item.code ?? `item-${idx}`}
                              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, mb: 0.75, bgcolor: item.isVisible ? 'background.paper' : 'action.hover', opacity: item.isVisible ? 1 : 0.6, pr: '180px' }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
                                <IconButton size="small" onClick={() => moveItemUp(activeCatCode, idx)} disabled={idx === 0}>
                                  <ExpandMoreIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => moveItemDown(activeCatCode, idx)} disabled={idx === itemsForCategory.length - 1}>
                                  <ExpandMoreIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              {imgSrc ? (
                                <Box component="img" src={imgSrc} alt={item.name || item.code} sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 1, mr: 1.5, flexShrink: 0, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }} />
                              ) : (
                                <Box sx={{ width: 40, height: 40, borderRadius: 1, mr: 1.5, flexShrink: 0, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <ItemsIcon fontSize="small" color="disabled" />
                                </Box>
                              )}
                              <ListItemText
                                primary={<Typography variant="body2" fontWeight={500}>{item.name || item.code}</Typography>}
                                secondary={<Typography variant="caption" color="text.secondary">Position: {idx + 1} · Code: {item.code}{hasAudio && ` · ${apiItem!.soundOptions!.length} voice(s)`}</Typography>}
                              />
                              <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title={hasAudio ? (playingItemCode === item.code ? 'Stop' : 'Play audio') : 'No audio stored'}>
                                  <span>
                                    <IconButton size="small" color={playingItemCode === item.code ? 'error' : 'primary'} onClick={() => playStandardItemAudio(item.code, item.name || item.code)} disabled={!hasAudio}>
                                      {playingItemCode === item.code ? <StopIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <FormControlLabel
                                  control={<Switch checked={item.isVisible} onChange={(e) => setItemVisible(activeCatCode, item.code, e.target.checked)} size="small" color="primary" />}
                                  label={<Typography variant="caption">{item.isVisible ? 'Visible' : 'Hidden'}</Typography>}
                                  labelPlacement="start"
                                  sx={{ ml: 0 }}
                                />
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </>
                )}
              </TabPanel>

              {/* ── Tab 4: Custom Items ── */}
              <TabPanel value={tab} index={4}>
                {showAddCustom ? (
                  <Box sx={{ border: '1px solid', borderColor: 'primary.light', borderRadius: 2, p: 2, mb: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>{editingCustomItemId ? 'Edit Custom Item' : 'Add Custom Item'}</Typography>
                    {genError && <Alert severity="error" sx={{ mb: 1.5, py: 0 }} onClose={() => setGenError(null)}>{genError}</Alert>}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <TextField
                        label="Name *" size="small" fullWidth value={newCustomName}
                        onChange={(e) => {
                          setNewCustomName(e.target.value);
                          if (!newCustomCodeEdited) setNewCustomCode(toPascalCase(e.target.value));
                          if (!newCustomAudioTextEdited) setNewCustomAudioText(e.target.value);
                        }}
                      />
                      <TextField
                        label="Code *" size="small" fullWidth value={newCustomCode}
                        onChange={(e) => { setNewCustomCode(e.target.value); setNewCustomCodeEdited(true); }}
                        error={customCodeDuplicate(newCustomCode)}
                        helperText={customCodeDuplicate(newCustomCode) ? 'A custom item with this code already exists' : 'Unique identifier (auto-filled from name)'}
                      />
                      <TextField
                        label="Category *" size="small" select fullWidth value={newCustomCat}
                        onChange={(e) => setNewCustomCat(e.target.value)}
                        SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}
                      >
                        {allCatsForPicker.map((c) => (<MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>))}
                      </TextField>
                      <TextField label="Description" size="small" fullWidth value={newCustomDesc} onChange={(e) => setNewCustomDesc(e.target.value)} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            label="Audio Text" size="small" fullWidth value={newCustomAudioText}
                            onChange={(e) => { setNewCustomAudioText(e.target.value); setNewCustomAudioTextEdited(true); }}
                            helperText="Text used for speech synthesis on the mobile app"
                          />
                          <Tooltip title={!newCustomAudioText.trim() ? 'Enter audio text first' : genAudioLoading ? 'Generating…' : newCustomMultiVoiceAudio.length > 0 ? `Regenerate (${newCustomMultiVoiceAudio.length} voice${newCustomMultiVoiceAudio.length !== 1 ? 's' : ''} stored)` : `Generate audio for all ${allVoices.length} voice${allVoices.length !== 1 ? 's' : ''}`}>
                            <span>
                              <Button
                                size="small"
                                variant={newCustomMultiVoiceAudio.length > 0 ? 'contained' : 'outlined'}
                                color={newCustomMultiVoiceAudio.length > 0 ? 'success' : 'primary'}
                                onClick={generateCustomItemAudio}
                                disabled={!newCustomAudioText.trim() || genAudioLoading}
                                startIcon={genAudioLoading ? <CircularProgress size={14} color="inherit" /> : <AudioIcon />}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: '-18px', flexShrink: 0 }}
                              >
                                {newCustomMultiVoiceAudio.length > 0 ? `${newCustomMultiVoiceAudio.length} voice${newCustomMultiVoiceAudio.length !== 1 ? 's' : ''}` : 'Gen Audio'}
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Image</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {newCustomImageData ? (
                            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                              <Box component="img" src={newCustomImageData} alt="preview" sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                              <IconButton size="small" onClick={() => setNewCustomImageData('')} sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', width: 20, height: 20 }}>
                                <DeleteIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ width: 64, height: 64, borderRadius: 1, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <AudioIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                            </Box>
                          )}
                          <Tooltip title={!newCustomName.trim() ? 'Enter a name first' : genImageLoading ? 'Generating…' : 'Generate image with AI'}>
                            <span>
                              <Button
                                size="small" variant="outlined" color="secondary"
                                onClick={generateCustomItemImage}
                                disabled={!newCustomName.trim() || genImageLoading}
                                startIcon={genImageLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                                sx={{ textTransform: 'none' }}
                              >
                                {genImageLoading ? 'Generating…' : 'Generate Image'}
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={() => { resetCustomItemForm(); setShowAddCustom(false); }} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button
                          size="small" variant="contained" onClick={addCustomItem}
                          disabled={!newCustomName.trim() || !newCustomCode.trim() || !newCustomCat || genAudioLoading || genImageLoading || customCodeDuplicate(newCustomCode)}
                          sx={{ textTransform: 'none' }}
                        >
                          {editingCustomItemId ? 'Save Changes' : 'Add Item'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddCustom(true)} sx={{ textTransform: 'none', mb: 2 }}>
                    Add Custom Item
                  </Button>
                )}
                {customItems.length === 0 ? (
                  <Alert severity="info">No custom items in this configuration.</Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Custom Items</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      Audio is played from stored data — no external API calls. Use the delete button to remove a custom item.
                    </Typography>
                    <List dense disablePadding>
                      {customItems.map((item, idx) => (
                        <ListItem key={item.id ?? item.code ?? `custom-${idx}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, mb: 0.75, bgcolor: 'background.paper', alignItems: 'center' }}>
                          {item.imageData ? (
                            <Box component="img" src={item.imageData.startsWith('data:') ? item.imageData : `data:image/png;base64,${item.imageData}`} alt={item.name} sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, mr: 1.5, flexShrink: 0 }} />
                          ) : (
                            <Box sx={{ width: 48, height: 48, borderRadius: 1, mr: 1.5, bgcolor: 'action.hover', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <AudioIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                            </Box>
                          )}
                          <ListItemText
                            primary={<Typography variant="body2" fontWeight={500}>{item.name}</Typography>}
                            secondary={<Typography variant="caption" color="text.secondary">{item.description ? `${item.description} · ` : ''}Category: {item.categoryCode}{item.multiVoiceAudioData && item.multiVoiceAudioData.length > 0 && ` · ${item.multiVoiceAudioData.length} voice(s) stored`}</Typography>}
                          />
                          <ListItemSecondaryAction sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={!item.multiVoiceAudioData || item.multiVoiceAudioData.length === 0 ? 'No audio stored' : playingId === item.id ? 'Stop' : 'Play audio'}>
                              <span>
                                <IconButton size="small" color={playingId === item.id ? 'error' : 'primary'} onClick={() => playCustomItemAudio(item)} disabled={!item.multiVoiceAudioData || item.multiVoiceAudioData.length === 0}>
                                  {playingId === item.id ? <StopIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Edit item">
                              <IconButton size="small" color="default" onClick={() => startEditCustomItem(item)}><EditIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Remove from this configuration">
                              <IconButton size="small" color="error" onClick={() => deleteCustomItem(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </TabPanel>

              {/* ── Tab 5: Favorites ── */}
              <TabPanel value={tab} index={5}>
                {showAddFav ? (
                  <Box sx={{ border: '1px solid', borderColor: 'primary.light', borderRadius: 2, p: 2, mb: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Add Favorite</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <TextField
                        label="Category *" size="small" select fullWidth value={newFavCat}
                        onChange={(e) => { setNewFavCat(e.target.value); setNewFavItem(''); }}
                        SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}
                      >
                        {allCatsForPicker.map((c) => (<MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>))}
                      </TextField>
                      <Autocomplete
                        options={itemsForFavPicker}
                        getOptionLabel={(o) => o.name}
                        getOptionDisabled={(o) => (config.favorites ?? []).some((f) => f.itemCode === o.code && f.categoryCode === newFavCat)}
                        value={itemsForFavPicker.find((i) => i.code === newFavItem) ?? null}
                        onChange={(_, val) => setNewFavItem(val?.code ?? '')}
                        disabled={!newFavCat}
                        size="small"
                        fullWidth
                        renderInput={(params) => (<TextField {...params} label={newFavCat ? `Item * (${itemsForFavPicker.length})` : 'Item *'} placeholder="Search…" />)}
                        ListboxProps={{ sx: { maxHeight: 260 } }}
                        noOptionsText="No items found"
                      />
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={() => { setShowAddFav(false); setNewFavCat(''); setNewFavItem(''); }} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={addFavorite} disabled={!newFavCat || !newFavItem} sx={{ textTransform: 'none' }}>Add Favorite</Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddFav(true)} sx={{ textTransform: 'none', mb: 2 }}>
                    Add Favorite
                  </Button>
                )}
                {favorites.length === 0 ? (
                  <Alert severity="info">No favorites saved in this configuration.</Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Favorited Items</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      These item codes are marked as favorites in the app. Remove items you no longer want favorited.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {favorites.map((fav) => (
                        <Chip
                          key={`${fav.categoryCode}-${fav.itemCode}`}
                          label={`${fav.itemCode}${fav.prefixItemCode ? ` (${fav.prefixItemCode})` : ''}`}
                          icon={<FavoriteIcon />}
                          onDelete={() => removeFavorite(fav.itemCode)}
                          color={fav.isAutoGenerated ? 'primary' : 'default'}
                          variant="outlined"
                          size="small"
                          title={`Category: ${fav.categoryCode} · Source: ${fav.source}`}
                        />
                      ))}
                    </Box>
                  </>
                )}
              </TabPanel>

            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {saveError && <Alert severity="error" sx={{ py: 0 }} onClose={() => setSaveError(null)}>{saveError}</Alert>}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} variant="text" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 110 }}
          >
            {saving ? (isCreateMode ? 'Creating…' : 'Saving…') : (isCreateMode ? 'Create' : 'Save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Section Component ───────────────────────────────────────────────────

interface TenantConfigurationsSectionProps {
  user: User;
}

export const TenantConfigurationsSection: React.FC<TenantConfigurationsSectionProps> = ({ user }) => {
  const tenants = useMemo(
    () => (user.mappings ?? []).map((m) => m.tenantId).filter(Boolean),
    [user.mappings]
  );

  const [selectedTenantId, setSelectedTenantId] = useState<string>(tenants[0] ?? '');

  // Keep selectedTenantId valid when mappings change
  React.useEffect(() => {
    if (selectedTenantId && !tenants.includes(selectedTenantId)) {
      setSelectedTenantId(tenants[0] ?? '');
    }
    if (!selectedTenantId && tenants.length > 0) {
      setSelectedTenantId(tenants[0]);
    }
  }, [tenants, selectedTenantId]);

  // ── saved overrides for immediate list update ──
  const [savedOverrides, setSavedOverrides] = useState<Record<string, TenantCustomization>>({});

  // ── dialog state ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomization, setEditingCustomization] = useState<TenantCustomization | null>(null);

  // ── delete confirmation state ──
  const [deleteConfirm, setDeleteConfirm] = useState<TenantCustomization | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── snackbar ──
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ── GraphQL ──
  const { data: voicesData } = useQuery(GET_VOICES, { fetchPolicy: 'cache-first' });
  const allVoices: Voice[] = voicesData?.voices ?? [];

  const { data, loading, error, refetch } = useQuery(GET_TENANT_CUSTOMIZATIONS, {
    variables: { tenantId: selectedTenantId },
    skip: !selectedTenantId,
    fetchPolicy: 'cache-and-network',
  });

  const [deleteCustomization] = useMutation(DELETE_TENANT_CUSTOMIZATION);

  // Merge fetched data with saved overrides so list updates immediately after save
  const customizations = useMemo((): TenantCustomization[] => {
    const base: TenantCustomization[] = data?.tenantCustomizations ?? [];
    const overrideList = Object.values(savedOverrides);

    // New records (not in base yet)
    const newRecords = overrideList.filter((o) => !base.find((b) => b.id === o.id));

    // Merged: base records updated with any overrides
    const merged = base.map((b) => savedOverrides[b.id] ?? b);

    return [...newRecords, ...merged];
  }, [data, savedOverrides]);

  // ── handlers ──
  const handleOpenCreate = () => {
    setEditingCustomization(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (c: TenantCustomization) => {
    setEditingCustomization(c);
    setDialogOpen(true);
  };

  const handleSaved = useCallback((saved: TenantCustomization) => {
    setSavedOverrides((prev) => ({ ...prev, [saved.id]: saved }));
    showSnack(editingCustomization ? 'Customization updated' : 'Customization created');
    refetch();
  }, [editingCustomization, refetch]);

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm || !selectedTenantId) return;
    setDeleteError(null);
    try {
      await deleteCustomization({ variables: { id: deleteConfirm.id, tenantId: selectedTenantId } });
      setSavedOverrides((prev) => {
        const next = { ...prev };
        delete next[deleteConfirm.id];
        return next;
      });
      setDeleteConfirm(null);
      showSnack('Customization deleted');
      refetch();
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // ── No tenants ──
  if (tenants.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No tenant mappings found for your account.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
          <TenantIcon sx={{ color: 'secondary.main', flexShrink: 0 }} />
          <Typography variant="h6" fontWeight={600} noWrap>
            Tenant Configurations
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          disabled={!selectedTenantId}
          sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          New Configuration
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* Tenant selector */}
      {tenants.length > 1 && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f0f4ff', borderRadius: 2, border: '1px solid #c5cae9' }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Select Tenant
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Select a tenant to view and manage its configurations.
          </Typography>
          <Autocomplete
            size="small"
            sx={{ minWidth: 280, maxWidth: 420 }}
            options={tenants}
            getOptionLabel={(tid) => tid}
            value={selectedTenantId || undefined}
            onChange={(_, newVal) => {
              setSelectedTenantId(newVal ?? '');
              setSavedOverrides({});
            }}
            isOptionEqualToValue={(opt, val) => opt === val}
            disableClearable
            renderInput={(params) => (
              <TextField {...params} label="Tenant" placeholder="Select tenant…" />
            )}
            noOptionsText="No tenants found"
          />
        </Box>
      )}

      {/* Config list */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Alert severity="error">Failed to load configurations: {error.message}</Alert>
      ) : customizations.length === 0 ? (
        <Alert severity="info">
          No customizations found for this tenant. Create one to get started.
        </Alert>
      ) : (
        <List disablePadding>
          {customizations.map((c) => (
            <ListItem
              key={c.id}
              disablePadding
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1.5, p: 0, overflow: 'hidden' }}
            >
              <Box sx={{ width: '100%', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {c.label || '(no label)'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      Last updated: {formatDate(c.updatedDate)} · v{c.version}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                      <Chip
                        label={CUSTOMIZATION_TYPE_OPTIONS.find((o) => o.value === c.customizationType)?.label ?? c.customizationType}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem', height: 22 }}
                      />
                      {!c.isActive && (
                        <Chip label="Inactive" size="small" color="default" variant="outlined" sx={{ fontSize: '0.72rem', height: 22 }} />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title="Edit configuration">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(c)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete configuration">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirm(c)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      )}

      {/* Edit / Create Dialog */}
      <EditDialog
        open={dialogOpen}
        customization={editingCustomization}
        allCustomizations={customizations}
        allVoices={allVoices}
        tenantId={selectedTenantId}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />

      {/* Delete Confirmation */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => { setDeleteConfirm(null); setDeleteError(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Configuration?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete &quot;{deleteConfirm?.label || '(no label)'}&quot;? This cannot be undone.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>{deleteError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setDeleteConfirm(null); setDeleteError(null); }}
            variant="text"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TenantConfigurationsSection;
